import { useCallback, useMemo } from 'react';
import { useOverviewState } from './useOverviewState';
import type { PeopleFilters, Status } from '../utils/panorama/panoramaPeopleRows';
import { STATUS_OPTIONS } from '../pages/VacationOverviewPage/vacationOverviewPage.definitions';

export type OverviewScreen = 'dashboard' | 'table';

export const SCREEN_KEY = 'visao';

export const TABLE_FILTER_KEYS = {
	search: 't_busca',
	statuses: 't_situacao',
	clients: 't_cliente',
	periodFrom: 't_de',
	periodTo: 't_ate',
	prazoMes: 't_prazo',
	stages: 't_etapa',
} as const;

const VALID_DATE = /^\d{4}-\d{2}-\d{2}$/;
const VALID_STAGE = /^(manager|client|dp|correction)$/;

const VALID_STATUSES = STATUS_OPTIONS.map(o => o.value) as string[];

function validOrNull(value: string | null, pattern: RegExp): string | null {
	return value && pattern.test(value) ? value : null;
}

export function useOverviewScreenFilters() {
	const { get, getAll, setMany } = useOverviewState();

	const screen: OverviewScreen = get(SCREEN_KEY) === 'table' ? 'table' : 'dashboard';

	const searchStr = get(TABLE_FILTER_KEYS.search) || '';
	const statusesStr = getAll(TABLE_FILTER_KEYS.statuses).join('|');
	const clientsStr = getAll(TABLE_FILTER_KEYS.clients).join('|');
	const periodFromStr = validOrNull(get(TABLE_FILTER_KEYS.periodFrom), VALID_DATE) || '';
	const periodToStr = validOrNull(get(TABLE_FILTER_KEYS.periodTo), VALID_DATE) || '';
	const prazoMesStr = validOrNull(get(TABLE_FILTER_KEYS.prazoMes), /^\d{4}-\d{2}$/) || '';
	const stagesStr = getAll(TABLE_FILTER_KEYS.stages).join('|');

	const tableFilters = useMemo<PeopleFilters>(
		() => ({
			search: searchStr,
			statuses: getAll(TABLE_FILTER_KEYS.statuses).filter(s => VALID_STATUSES.includes(s)) as Status[],
			clients: clientsStr ? clientsStr.split('|') : [],
			periodFrom: validOrNull(periodFromStr, VALID_DATE),
			periodTo: validOrNull(periodToStr, VALID_DATE),
			prazoMes: validOrNull(prazoMesStr, /^\d{4}-\d{2}$/),
			stages: getAll(TABLE_FILTER_KEYS.stages).filter(s => VALID_STAGE.test(s)) as Array<'manager' | 'client' | 'dp' | 'correction'> | null,
		}),
		[searchStr, statusesStr, clientsStr, periodFromStr, periodToStr, prazoMesStr, stagesStr]
	);

	const setScreen = useCallback(
		(next: OverviewScreen) => setMany({ [SCREEN_KEY]: next === 'table' ? 'table' : null }),
		[setMany]
	);

	const setTableFilters = useCallback(
		(next: PeopleFilters) => {
			setMany({
				[TABLE_FILTER_KEYS.search]: next.search || null,
				[TABLE_FILTER_KEYS.statuses]: next.statuses ?? [],
				[TABLE_FILTER_KEYS.clients]: next.clients ?? [],
				[TABLE_FILTER_KEYS.periodFrom]: next.periodFrom ?? null,
				[TABLE_FILTER_KEYS.periodTo]: next.periodTo ?? null,
				[TABLE_FILTER_KEYS.prazoMes]: next.prazoMes ?? null,
				[TABLE_FILTER_KEYS.stages]: next.stages ?? [],
			});
		},
		[setMany]
	);

	const showTableWithStatuses = useCallback(
		(statuses: Status[], filters?: { periodFrom?: string | null; periodTo?: string | null; clients?: string[]; stages?: Array<'manager' | 'client' | 'dp' | 'correction'>; prazoMes?: string | null }) => {
			const updates: Record<string, any> = {
				[SCREEN_KEY]: 'table',
				[TABLE_FILTER_KEYS.search]: null,
				[TABLE_FILTER_KEYS.clients]: [],
				[TABLE_FILTER_KEYS.periodFrom]: null,
				[TABLE_FILTER_KEYS.periodTo]: null,
				[TABLE_FILTER_KEYS.prazoMes]: null,
				[TABLE_FILTER_KEYS.stages]: [],
				[TABLE_FILTER_KEYS.statuses]: statuses,
			};
			if (filters?.periodFrom !== undefined) updates[TABLE_FILTER_KEYS.periodFrom] = filters.periodFrom;
			if (filters?.periodTo !== undefined) updates[TABLE_FILTER_KEYS.periodTo] = filters.periodTo;
			if (filters?.clients !== undefined) updates[TABLE_FILTER_KEYS.clients] = filters.clients;
			if (filters?.stages !== undefined) updates[TABLE_FILTER_KEYS.stages] = filters.stages;
			if (filters?.prazoMes !== undefined) updates[TABLE_FILTER_KEYS.prazoMes] = filters.prazoMes;
			setMany(updates);
		},
		[setMany]
	);

	return { screen, setScreen, tableFilters, setTableFilters, showTableWithStatuses };
}
